import React from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik, setIn } from 'formik';

import { useClaim } from '~/components/ClaimContainer';
import mixpanel from '~/components/CmsMain/mixpanel';
import CommunicationCard from '~/components/communications/CommunicationCard';
import CommunicationActionSendNew from '~/components/communications/CommunicationCard/CommunicationActionSendNew';
import useCreateCommunication from '~/components/communications/CommunicationCard/useCreateCommunication';
import EmailCommunicationSpecificBody from '~/components/communications/EmailCommunicationCard/EmailCommunicationSpecificBody';
import {
  getEmailMessageNotAttachedToClaimValidation,
  getEmailMessageValidation,
} from '~/components/communications/EmailCommunicationCard/schemaValidations';
import { getNamesOfMissingEmailsFromRecipients } from '~/components/communications/EmailCommunicationCard/utils';
import { Text } from '~/components/core';
import {
  FROM_TEMPLATE_FORM_KEYS,
  htmlToTextTemplateParser,
} from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import { useCms } from '~/components/hooks/useCms';
import LoadingDialog from '~/components/LoadingDialog';
import useOrganization from '~/components/OrganizationContext';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { getExposuresArrayFromClaimByIds, reportAxiosError } from '~/Utils';

import useEmailSignatureHtml from './useEmailSignatureHtml';

const SendEmailCommunicationCardContainer = (props) => {
  const {
    contact,
    communication,
    cc_contacts,
    bcc_contacts,
    contactEmailId,
    onClose,
    onSendEmail,
    emailTitle,
    emailText,
    summary,
    attachments,
    exposureIds,
    forcedRecipient, // forcedRecipient is given in case of reply email of unknown claim
    onSubmitOverride,
    referredCommunicationId,
    open,
    onMinimized,
    disableMinimized,
    isNotClaimRelated = false,
    shouldDisplaySenderEmail = true,
    shouldDisplaySupervisorSenderSelection = false,
    communicationHeaderAdditionalProps = {},
    communicationSuggestionNotificationId = null,
    AdditionalDialogHeader = <React.Fragment />,
    shouldOverrideTitleWithTemplate,
    shouldContactInitialBodyWithTemplate,
    documentTemplateId,
    genericTemplateId,
    parentAttachments,
    initialFailedTokens,
    moiId,
    moiExposureId,
  } = props;
  const { communicationConfiguration } = useOrganization();
  const isCategoryRequired = communicationConfiguration?.is_category_required ?? false;
  const validationSchema = forcedRecipient
    ? getEmailMessageNotAttachedToClaimValidation({ isCategoryRequired })
    : getEmailMessageValidation('Outgoing', isNotClaimRelated, { isCategoryRequired });
  const { claim } = useClaim(); // claim may be undefined - In case of reply to email of an unknown claim
  const { userOrganization, user } = useCms();

  const {
    isLoading,
    isError,
    data: emailConfigHtml,
  } = useEmailSignatureHtml({
    claimId: claim?.id,
    referredCommunicationId,
    shouldFetch: !emailText || !emailTitle || shouldDisplaySupervisorSenderSelection || !!genericTemplateId,
  });
  const { communicationCommonInitialValues } = useCreateCommunication('email', {
    summary,
    contact,
    exposure_ids: exposureIds,
  });

  if (isLoading || isError) {
    return <LoadingDialog isError={isError} isLoading={isLoading} track="Send Email" />;
  }

  const emailSubject = emailTitle || htmlToTextTemplateParser(emailConfigHtml?.email_title, false);

  const orgDefaultSignatureHtml = emailConfigHtml?.user_email_signature_html;
  // Whatever the opening flow pre-seeded the body with: the forwarded block from the forward dialog,
  // the quoted original from a reply, or '' for a new email. Passed down as `initialBody`, which is
  // the name the body component knows it by -- one value, three names across the two files.
  const emailBody =
    emailText || (!shouldDisplaySupervisorSenderSelection ? `<div></div><div></div>${orgDefaultSignatureHtml}` : '');

  return (
    <Formik
      initialValues={{
        ...communicationCommonInitialValues,
        cc_contacts: cc_contacts || [],
        bcc_contacts: bcc_contacts || [],
        subject: emailSubject,
        body_html: shouldDisplaySupervisorSenderSelection ? '' : emailBody,
        attached_documents: attachments || [],
        email_id: contact ? contactEmailId || (contact.primary_email && contact.primary_email.id) : '',
        [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_ID]: null,
        [FROM_TEMPLATE_FORM_KEYS.TEMPLATE_CONTEXT]: null,
        communication_suggestion_claim_notification_id: communicationSuggestionNotificationId,
        send_as_user_id: user?.id || null,
        include_parent_attachments: parentAttachments && parentAttachments?.length > 0,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, formikProps) => {
        const exposures = getExposuresArrayFromClaimByIds(claim, values.exposure_ids);

        if (onSubmitOverride) {
          return onSubmitOverride(values)
            .then(() => {
              formikProps.setSubmitting(false);
              if (claim) {
                mixpanel.track(MIXPANEL_EVENTS.EMAIL_COMMUNICATION_SENT, {
                  ...mixpanel.getMixpanelAttributes(claim, exposures, {
                    with_reminder: values.add_follow_up_reminder,
                    contact_id: values.contact.id,
                    attached_files: [
                      ...values.attached_documents.map(
                        (attachment) =>
                          `File Name: ${attachment.file_name}; File Size: ${attachment.stored_file.size}; File Type: ${attachment.stored_file.mime_type}`
                      ),
                    ],
                  }),
                });
              } else {
                mixpanel.track(MIXPANEL_EVENTS.EMAIL_COMMUNICATION_SENT, {
                  with_reminder: values.add_follow_up_reminder,
                  contact_id: values.contact?.id,
                  attached_files: [
                    ...values.attached_documents.map(
                      (attachment) =>
                        `File Name: ${attachment.file_name}; File Size: ${attachment.stored_file.size}; File Type: ${attachment.stored_file.mime_type}`
                    ),
                  ],
                });
              }
            })
            .catch(async (error) => {
              formikProps.setSubmitting(false);
              await reportAxiosError(error);
            });
        }

        let url;
        let valuesToSend = values;

        if (forcedRecipient) {
          url = '/api/v1/communications/reply_to_not_claim_related_email';
          valuesToSend = {
            ...valuesToSend,
            forced_contact: forcedRecipient.optionalContact,
            forced_contact_email: forcedRecipient.optionalContactEmail,
            email_address: forcedRecipient.emailAddress,
            referred_communication_id: referredCommunicationId,
            is_reply: true,
          };
        } else {
          url = `/api/v1/claims/${claim.id}/contacts/${values.contact.id}/emails/${values['email_id']}/send_email`;
          valuesToSend = {
            ...valuesToSend,
            is_reply: !!referredCommunicationId,
            referred_communication_id: referredCommunicationId,
          };
        }

        return axios
          .post(url, valuesToSend)
          .then(async (res) => {
            await onSendEmail(res.data);
            if (claim) {
              mixpanel.track(MIXPANEL_EVENTS.EMAIL_COMMUNICATION_SENT, {
                ...mixpanel.getMixpanelAttributes(claim, exposures, {
                  with_reminder: values.add_follow_up_reminder,
                  contact_id: values.contact.id,
                  communication_id: res.data.id,
                  attached_files: [
                    ...values.attached_documents.map(
                      (attachment) =>
                        `File Name: ${attachment.file_name}; File Size: ${attachment.stored_file.size}; File Type: ${attachment.stored_file.mime_type}`
                    ),
                  ],
                  status: res.status === 200 ? 'Success' : 'Failed',
                  http_response_code: res.status,
                }),
              });
            } else {
              mixpanel.track(MIXPANEL_EVENTS.EMAIL_COMMUNICATION_SENT, {
                with_reminder: values.add_follow_up_reminder,
                contact_id: values.contact?.id,
                communication_id: res.data.id,
                attached_files: [
                  ...values.attached_documents.map(
                    (attachment) =>
                      `File Name: ${attachment.file_name}; File Size: ${attachment.stored_file.size}; File Type: ${attachment.stored_file.mime_type}`
                  ),
                ],
                status: res.status === 200 ? 'Success' : 'Failed',
                http_response_code: res.status,
              });
            }
          })
          .catch(async (error) => {
            formikProps.setSubmitting(false);
            await reportAxiosError(error);
          });
      }}
    >
      {(formikProps) => {
        const { values, setFieldTouched } = formikProps;

        return (
          <CommunicationCard
            title="New Email"
            communication={communication}
            senderFragment={
              userOrganization?.is_claims_inbox_enabled && shouldDisplaySenderEmail ? (
                <SenderFragment userSignatureHtmlData={emailConfigHtml} />
              ) : (
                <></>
              )
            }
            communicationInitialFields={{
              communicationChannel: 'email',
              communicationContact: values.contact,
              communicationDirection: 'Outgoing',
            }}
            CommunicationTypeSpecificBody={EmailCommunicationSpecificBody}
            communicationTypeSpecificBodyProps={{
              forcedRecipient,
              isNotClaimRelated: !!forcedRecipient || isNotClaimRelated,
              emailSignature: orgDefaultSignatureHtml,
              shouldOverrideTitleWithTemplate,
              shouldContactInitialBodyWithTemplate,
              shouldDisplaySupervisorSenderSelection,
              initialBody: emailBody,
              initialExposureId: exposureIds ? exposureIds[0] : undefined,
              initialDocumentTemplateId: documentTemplateId,
              initialGenericTemplateId: genericTemplateId,
              parentAttachments,
              referredCommunicationId,
              initialFailedTokens,
              moiId,
              moiExposureId,
            }}
            communicationHeaderAdditionalProps={{
              outOfContactsContextSearch: !claim,
              isDynamicContact: !contact && !forcedRecipient,
              dynamicContactLabel: 'Send To',
              getValuesOnSelectContact: (contact, valuesWithContact) => {
                const email = !contact || contact.id === '' || !contact.emails?.length ? undefined : contact.emails[0];
                const withEmailId = setIn(valuesWithContact, 'email_id', email ? email.id : '');
                return setIn(withEmailId, 'email_address', email ? email.email : '');
              },
              onSelectContact: () => {
                // touched-only, so pass shouldValidate=false: this runs after the contact field's
                // atomic setValues and would otherwise re-validate the pre-selection snapshot
                setFieldTouched('email_address', '', false);
                setFieldTouched('email_id', '', false);
              },
              ...communicationHeaderAdditionalProps,
            }}
            isDialog
            isNew
            maxWidth="lg"
            onClose={onClose}
            CommunicationAction={CommunicationActionSendNew}
            communicationActionProps={{
              onSubmit: () => {
                formikProps.handleSubmit();
              },
              isSubmitting: formikProps.isSubmitting,
              disabled: getNamesOfMissingEmailsFromRecipients(values['cc_contacts']).length > 0,
            }}
            open={open}
            onMinimized={onMinimized}
            disableMinimized={disableMinimized}
            AdditionalDialogHeader={AdditionalDialogHeader}
          />
        );
      }}
    </Formik>
  );
};

const SenderFragment = ({ userSignatureHtmlData }) => {
  return (
    <div className="flex items-center justify-center">
      <Text variant={Text.VARIANTS.XS}>
        <b>From:</b> {userSignatureHtmlData?.sender_email_address}
      </Text>
    </div>
  );
};

SenderFragment.propTypes = {
  userSignatureHtmlData: {
    sender_email_address: PropTypes.string,
  },
};

SendEmailCommunicationCardContainer.propTypes = {
  contact: PropTypes.object,
  communication: PropTypes.object,
  cc_contacts: PropTypes.array,
  bcc_contacts: PropTypes.array,
  contactEmailId: PropTypes.number,
  onSendEmail: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onMinimized: PropTypes.func,
  attachments: PropTypes.array,
  emailTitle: requiredIf(PropTypes.string, (props) => props.forcedRecipient),
  emailText: PropTypes.string,
  summary: PropTypes.string,
  exposureIds: PropTypes.array,
  forcedRecipient: PropTypes.object,
  onSubmitOverride: PropTypes.func,
  referredCommunicationId: PropTypes.number,
  isNotClaimRelated: PropTypes.bool,
  shouldDisplaySenderEmail: PropTypes.bool,
  shouldDisplaySupervisorSenderSelection: PropTypes.bool,
  disableMinimized: PropTypes.bool,
  open: PropTypes.bool,
  communicationHeaderAdditionalProps: PropTypes.object,
  communicationSuggestionNotificationId: PropTypes.number,
  AdditionalDialogHeader: PropTypes.node,
  shouldOverrideTitleWithTemplate: PropTypes.bool,
  shouldContactInitialBodyWithTemplate: PropTypes.bool,
  documentTemplateId: PropTypes.number,
  genericTemplateId: PropTypes.number,
  parentAttachments: PropTypes.array,
  initialFailedTokens: PropTypes.arrayOf(PropTypes.string),
  moiId: PropTypes.number,
  moiExposureId: PropTypes.number,
};

export default SendEmailCommunicationCardContainer;
